import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useCurrentOrganization } from '~/hooks'
import { MutationOf, useTRPC } from '~/services/trpc'

import { SignalingMessage, useSignaling } from '../signaling'

type AnalyticsExportInput = Parameters<MutationOf<'analytics.export'>['mutateAsync']>[0]

interface UseAnalyticsExportJobOptions {
  onUpdate?: (progress: string, message: SignalingMessage) => void
  onComplete?: (url: string, message: SignalingMessage) => void
  onFailed?: (message: SignalingMessage) => void
  onConnectionUnavailable?: () => void
  onConnectionClosed?: () => void
}

function getProgress(payload: Record<string, unknown> | undefined) {
  const progress = typeof payload?.progress === 'number' ? payload.progress : 0
  const total = typeof payload?.total === 'number' ? payload.total : 0

  if (total <= 0) {
    return '0'
  }

  return ((progress / total) * 100).toFixed(2)
}

export function useAnalyticsExportJob(options: UseAnalyticsExportJobOptions = {}) {
  const trpc = useTRPC()
  const organization = useCurrentOrganization()
  const { send, status, subscribeToJob } = useSignaling()
  const mutation = useMutation(trpc.analytics.export.mutationOptions())
  const activeJobRef = useRef<{
    jobId: string
    unsubscribe: () => void
  } | null>(null)
  const [isRunning, setIsRunning] = useState(false)
  const [progress, setProgress] = useState('0')

  const clearActiveJob = useCallback(() => {
    activeJobRef.current?.unsubscribe()
    activeJobRef.current = null
  }, [])

  useEffect(() => clearActiveJob, [clearActiveJob])

  useEffect(() => {
    if (status !== 'closed' || !activeJobRef.current) {
      return
    }

    clearActiveJob()
    setIsRunning(false)
    setProgress('0')
    options.onConnectionClosed?.()
  }, [clearActiveJob, options, status])

  const startExport = useCallback(
    async (input: AnalyticsExportInput) => {
      if (activeJobRef.current || mutation.isPending || !organization?.id) {
        return null
      }

      const jobId = await mutation.mutateAsync(input)

      const unsubscribe = subscribeToJob(jobId, (message) => {
        if (message.type === 'update') {
          const nextProgress = getProgress(message.payload)

          setIsRunning(true)
          setProgress(nextProgress)
          options.onUpdate?.(nextProgress, message)
          return
        }

        if (message.type === 'complete') {
          const url = typeof message.payload?.url === 'string' ? message.payload.url : null

          clearActiveJob()
          setIsRunning(false)
          setProgress('0')

          if (url) {
            options.onComplete?.(url, message)
          }

          return
        }

        if (message.type === 'failed') {
          clearActiveJob()
          setIsRunning(false)
          setProgress('0')
          options.onFailed?.(message)
        }
      })

      activeJobRef.current = { jobId, unsubscribe }
      setIsRunning(true)
      setProgress('0')

      const didSend = send({
        type: 'start_job',
        jobId,
        organizationId: organization.id,
      })

      if (!didSend) {
        clearActiveJob()
        setIsRunning(false)
        setProgress('0')
        options.onConnectionUnavailable?.()
        return null
      }

      return jobId
    },
    [clearActiveJob, mutation, options, organization?.id, send, subscribeToJob],
  )

  return {
    startExport,
    isRunning,
    progress,
    isPending: mutation.isPending,
  }
}
